/**
 * Typed WorkOS webhook event catalog. WorkOS emits events for User
 * Management, Authentication, Organizations, Directory Sync (SCIM),
 * SSO Connections, and invitations. Brew exposes 18 events across 7
 * categories.
 */

import type {
  InboundEventCategory,
  InboundEventMetadata,
} from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'
import {
  SUPPORTED_WORKOS_EVENTS,
  type SupportedWorkOSEventType,
} from './event-types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema

export type { SupportedWorkOSEventType }
export { SUPPORTED_WORKOS_EVENTS }

const SUPPORTED_WORKOS_EVENT_SET = new Set<string>(SUPPORTED_WORKOS_EVENTS)

/**
 * Slim user-facing common preamble. WorkOS event metadata (event id,
 * event type, object id/type) is intentionally absent — the extractor
 * still emits those keys into `triggerInstances.payload` for debugging
 * and the contact mapper reads them from there.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      'Recipient email derived from the WorkOS event. Synthetic placeholder when the event lacks one (organization / connection events).',
    exampleValue: 'alex@example.com',
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    description: 'User first name when present.',
    exampleValue: 'Alex',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    description: 'User last name when present.',
    exampleValue: 'Morgan',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) derived from the WorkOS event when available.',
    exampleValue: 1_710_000_000,
  },
]

const USER_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'emailVerified',
    type: 'boolean',
    required: false,
    description: 'Whether the user has verified their email.',
    exampleValue: true,
  },
]

const AUTHENTICATION_EXTRA_FIELDS: Array<TriggerField> = [
  // Device/network telemetry is policy-excluded from the user-facing
  // schema (no filter/split/merge-tag value; privacy-sensitive). The
  // extractor still emits both for the contact mapper / debugging.
  {
    key: 'ipAddress',
    type: 'string',
    required: false,
    description: 'IP address that initiated the auth attempt when present.',
    exampleValue: '203.0.113.5',
    internal: true,
  },
  {
    key: 'userAgent',
    type: 'string',
    required: false,
    description: 'Browser user agent when present.',
    exampleValue: 'Mozilla/5.0 (Macintosh)',
    internal: true,
  },
]

const ORGANIZATION_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'organizationName',
    type: 'string',
    required: false,
    description: 'WorkOS organization display name when set.',
    exampleValue: 'Acme Inc.',
  },
]

const ORG_MEMBERSHIP_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'role',
    type: 'string',
    required: false,
    description: 'Membership role when present.',
    exampleValue: 'admin',
  },
]

const DSYNC_USER_EXTRA_FIELDS: Array<TriggerField> = []

const CONNECTION_EXTRA_FIELDS: Array<TriggerField> = []

const INVITATION_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'role',
    type: 'string',
    required: false,
    description: 'Role granted by the invitation when set.',
    exampleValue: 'member',
  },
]

function buildPayloadSchema(
  extras: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extras],
  }
}

function buildSamplePayload(
  _eventType: SupportedWorkOSEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'alex@example.com',
    firstName: 'Alex',
    lastName: 'Morgan',
    ...payload,
  }
}

export const WORKOS_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> = [
  { id: 'user', label: 'User' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'organization', label: 'Organization' },
  { id: 'organization_membership', label: 'Organization membership' },
  { id: 'directory_sync', label: 'Directory Sync' },
  { id: 'connection', label: 'Connection' },
  { id: 'invitation', label: 'Invitation' },
]

export const WORKOS_EVENT_METADATA: Array<InboundEventMetadata> = [
  {
    eventType: 'user.created',
    title: 'WorkOS - User Created',
    description: 'Fires when a new WorkOS user is provisioned.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('user.created', {
      firstName: 'Alex',
      lastName: 'Morgan',
      emailVerified: false,
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Welcome email',
        description:
          'Greet the new user with onboarding next steps and a setup checklist.',
      },
    ],
  },
  {
    eventType: 'user.updated',
    title: 'WorkOS - User Updated',
    description: 'Fires when a WorkOS user record changes.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('user.updated', {
      emailVerified: true,
    }),
    useCases: [
      {
        title: 'Contact sync',
        description: 'Mirror profile changes into Brew contacts.',
      },
    ],
  },
  {
    eventType: 'user.deleted',
    title: 'WorkOS - User Deleted',
    description: 'Fires when a WorkOS user is deleted.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('user.deleted', {}),
    useCases: [
      {
        title: 'Offboarding email',
        description: 'Confirm offboarding + offer recovery.',
      },
    ],
  },
  {
    eventType: 'authentication.email_verification_succeeded',
    title: 'WorkOS - Email Verification Succeeded',
    description: 'Fires when a user successfully verifies their email address.',
    category: 'authentication',
    payloadSchema: buildPayloadSchema(AUTHENTICATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload(
      'authentication.email_verification_succeeded',
      {}
    ),
    useCases: [
      {
        title: 'Post-verification welcome',
        description:
          'Trigger the real welcome flow once the email is verified.',
      },
    ],
  },
  {
    eventType: 'authentication.password_failed',
    title: 'WorkOS - Password Failed',
    description: 'Fires when a password sign-in attempt fails.',
    category: 'authentication',
    payloadSchema: buildPayloadSchema(AUTHENTICATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('authentication.password_failed', {}),
    useCases: [
      {
        title: 'Rate-limit alert',
        description: 'Detect repeated failed sign-ins and alert ops.',
      },
      {
        title: 'Account-security email',
        description:
          'Notify the user that someone attempted to sign in with the wrong password.',
      },
    ],
  },
  {
    // Canonical WorkOS event name lives at the top level (not under
    // `authentication.*`). See https://workos.com/docs/events and the
    // `password_reset.succeeded` entry in @workos-inc/node's event
    // interface union. An earlier Brew catalog mis-named this as
    // `authentication.password_reset_succeeded` which WorkOS never
    // emits — every password-reset automation silently dropped.
    eventType: 'password_reset.succeeded',
    title: 'WorkOS - Password Reset Succeeded',
    description: 'Fires when a password reset completes.',
    category: 'authentication',
    payloadSchema: buildPayloadSchema(AUTHENTICATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('password_reset.succeeded', {}),
    useCases: [
      {
        title: 'Security confirmation',
        description: 'Confirm the password change with the user.',
      },
    ],
  },
  {
    eventType: 'authentication.magic_auth_succeeded',
    title: 'WorkOS - Magic Auth Succeeded',
    description: 'Fires when a magic-link sign-in completes.',
    category: 'authentication',
    payloadSchema: buildPayloadSchema(AUTHENTICATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload(
      'authentication.magic_auth_succeeded',
      {}
    ),
    useCases: [
      {
        title: 'New sign-in alert',
        description:
          'Notify the user that someone signed in via a fresh magic link.',
      },
    ],
  },
  {
    eventType: 'organization.created',
    title: 'WorkOS - Organization Created',
    description:
      'Receives a WorkOS organization creation. The payload has no recipient identity, so Brew records it as unresolved and starts no automation.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORGANIZATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization.created', {
      email: 'unknown+org_test_123@org_test.brew.local',
      organizationName: 'Acme Inc.',
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Organization receipt audit',
        description:
          'Confirm the organization event reached Recent Events without claiming a recipient action ran.',
      },
    ],
  },
  {
    eventType: 'organization.updated',
    title: 'WorkOS - Organization Updated',
    description:
      'Receives a WorkOS organization update. The payload has no recipient identity, so Brew records it as unresolved and starts no automation.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORGANIZATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization.updated', {
      email: 'unknown+org_test_123@org_test.brew.local',
    }),
    useCases: [
      {
        title: 'Organization update audit',
        description:
          'Confirm the metadata change reached Recent Events without claiming a contact sync or automation ran.',
      },
    ],
  },
  {
    eventType: 'organization.deleted',
    title: 'WorkOS - Organization Deleted',
    description:
      'Receives a WorkOS organization deletion. The payload has no recipient identity, so Brew records it as unresolved and starts no automation.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORGANIZATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization.deleted', {
      email: 'unknown+org_test_123@org_test.brew.local',
    }),
    useCases: [
      {
        title: 'Organization deletion audit',
        description:
          'Confirm the deletion reached Recent Events without claiming a member notification or cleanup ran.',
      },
    ],
  },
  {
    eventType: 'organization_membership.created',
    title: 'WorkOS - Membership Created',
    description: 'Fires when a user joins an organization.',
    category: 'organization_membership',
    payloadSchema: buildPayloadSchema(ORG_MEMBERSHIP_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization_membership.created', {
      role: 'member',
    }),
    useCases: [
      {
        title: 'Team-welcome email',
        description: 'Welcome the new teammate to the organization.',
      },
    ],
  },
  {
    eventType: 'organization_membership.updated',
    title: 'WorkOS - Membership Updated',
    description: 'Fires when an organization membership changes (e.g. role).',
    category: 'organization_membership',
    payloadSchema: buildPayloadSchema(ORG_MEMBERSHIP_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization_membership.updated', {
      role: 'admin',
    }),
    useCases: [
      {
        title: 'Role-change notification',
        description: 'Confirm role updates with the member and the owner.',
      },
    ],
  },
  {
    eventType: 'organization_membership.deleted',
    title: 'WorkOS - Membership Deleted',
    description: 'Fires when a user leaves or is removed from an organization.',
    category: 'organization_membership',
    payloadSchema: buildPayloadSchema(ORG_MEMBERSHIP_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('organization_membership.deleted', {}),
    useCases: [
      {
        title: 'Offboarding',
        description:
          'Send a graceful goodbye when a user leaves the workspace.',
      },
    ],
  },
  {
    eventType: 'dsync.user.created',
    title: 'WorkOS - Directory User Created',
    description: 'Fires when SCIM provisioning creates a directory user.',
    category: 'directory_sync',
    payloadSchema: buildPayloadSchema(DSYNC_USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('dsync.user.created', {
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'SCIM-driven contact sync',
        description: 'Mirror SCIM-provisioned users into Brew contacts.',
      },
    ],
  },
  {
    eventType: 'dsync.user.updated',
    title: 'WorkOS - Directory User Updated',
    description: 'Fires when SCIM provisioning updates a directory user.',
    category: 'directory_sync',
    payloadSchema: buildPayloadSchema(DSYNC_USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('dsync.user.updated', {}),
    useCases: [
      {
        title: 'SCIM-driven contact sync',
        description: 'Mirror SCIM updates into Brew contacts.',
      },
    ],
  },
  {
    eventType: 'dsync.user.deleted',
    title: 'WorkOS - Directory User Deleted',
    description: 'Fires when SCIM provisioning deletes a directory user.',
    category: 'directory_sync',
    payloadSchema: buildPayloadSchema(DSYNC_USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('dsync.user.deleted', {}),
    useCases: [
      {
        title: 'SCIM-driven offboarding',
        description: 'Drop deprovisioned users from Brew audiences.',
      },
    ],
  },
  {
    eventType: 'connection.activated',
    title: 'WorkOS - Connection Activated',
    description:
      'Receives an SSO connection activation. The payload has no recipient identity, so Brew records it as unresolved and starts no automation.',
    category: 'connection',
    payloadSchema: buildPayloadSchema(CONNECTION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('connection.activated', {
      email: 'unknown+conn_test_123@org_test.brew.local',
    }),
    useCases: [
      {
        title: 'Connection receipt audit',
        description:
          'Confirm the activation reached Recent Events without claiming an ops alert ran.',
      },
    ],
  },
  {
    eventType: 'invitation.accepted',
    title: 'WorkOS - Invitation Accepted',
    description: 'Fires when a user accepts an invitation.',
    category: 'invitation',
    payloadSchema: buildPayloadSchema(INVITATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('invitation.accepted', {
      role: 'member',
    }),
    useCases: [
      {
        title: 'Welcome-to-team',
        description:
          'Greet the newly joined teammate with workspace orientation.',
      },
    ],
  },
]

export function isSupportedWorkOSEvent(
  eventType: string
): eventType is SupportedWorkOSEventType {
  return SUPPORTED_WORKOS_EVENT_SET.has(eventType)
}
